// CollaborationWorkspacePage.tsx

import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Tabs, Loader, Center, Text, Button, Flex } from "@mantine/core";
import TitleComponent from "@/components/shared/TitleComponent";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "@/context/UserContext";
import SharedDocumentsTab from "./SharedDocumentsTab";
import MatchedProfileView from "@/pages/profile/MatchedView";
import NdaPage from "@/pages/nda/NdaPage"; // Import your existing NdaPage
import QuestionnaireTab from "./QuestionnaireTab";
import { IconArrowLeft, IconDownload } from "@tabler/icons-react";
import { generateProfilePDF } from "@/utils/pdfGenerator";

export default function CollaborationWorkspacePage() {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const { userDetails, hasProfile } = useUserContext();
  const [activeTab, setActiveTab] = useState<string | null>("overview");

  // Loading states
  const [loadingCollaboration, setLoadingCollaboration] = useState(true);
  const [loadingCompany, setLoadingCompany] = useState(false);

  // Data states
  const [collaboration, setCollaboration] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Derived state
  const otherCompanyId =
    collaboration?.requesterCompanyId === userDetails?.company?.id
      ? collaboration?.receiverCompanyId
      : collaboration?.requesterCompanyId;

  // 1. Fetch Collaboration (Always needed for context)
  const fetchCollaboration = useCallback(async () => {
    if (!hasProfile || !id) return;
    setLoadingCollaboration(true);
    try {
      const collabData = await trpc.collaboration.getById.query({ matchId: id });
      setCollaboration(collabData);
    } catch (err) {
      console.error("Error fetching collaboration:", err);
      handleTrpcError(err);
    } finally {
      setLoadingCollaboration(false);
    }
  }, [id, hasProfile]);

  // 2. Fetch Company (Lazy load)
  const fetchCompany = useCallback(async (companyId: string) => {
    setLoadingCompany(true);
    try {
      const companyData = await trpc.company.getById.query({ companyId });
      setCompany(companyData);
    } catch (err) {
      console.error("Error fetching company:", err);
      handleTrpcError(err);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  // Track visited tabs to lazy load content but keep it mounted
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(new Set(["overview"]));

  // Effect: Fetch Collaboration on mount
  useEffect(() => {
    fetchCollaboration();
  }, [fetchCollaboration]);

  // Effect: Fetch Company when needed (Overview tab or explicit check) and update visited tabs
  useEffect(() => {
    if (activeTab) {
      setVisitedTabs((prev) => {
        if (prev.has(activeTab)) return prev;
        return new Set(prev).add(activeTab);
      });
    }

    if (activeTab === "overview" && otherCompanyId && !company && !loadingCompany) {
      fetchCompany(otherCompanyId);
    }
  }, [activeTab, otherCompanyId, company, loadingCompany, fetchCompany]);

  if (loadingCollaboration) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Loader size="lg" />
        </Center>
      </Box>
    );
  }

  if (!collaboration) {
    return (
      <Box mih="100vh" pb={20}>
        <TitleComponent title="Collaboration Workspace" />
        <Center py="xl" mih="50vh">
          <Text c="slate.6">Collaboration not found</Text>
        </Center>
      </Box>
    );
  }

  const handleDownloadProfile = async () => {
    let targetCompany = company;

    // If company data is missing, fetch it first
    if (!targetCompany && otherCompanyId) {
      setDownloadingPDF(true); // Show loading on button immediately
      try {
        targetCompany = await trpc.company.getById.query({ companyId: otherCompanyId });
        setCompany(targetCompany);
      } catch (error) {
        handleTrpcError(error);
        setDownloadingPDF(false);
        return;
      }
    }

    if (!targetCompany) return;

    setDownloadingPDF(true);
    try {
      await generateProfilePDF(targetCompany);
    } catch (error) {
      console.error("Failed to generate PDF:", error);
    } finally {
      setDownloadingPDF(false);
    }
  };

  return (
    <Box style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {/* Header with Back Button and Title */}
      <Flex align="center" gap="md" px="md" py="md" style={{ borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}>
        <Button
          variant="outline"
          color="gray"
          p={8}
          onClick={() => navigate("/collaborations")}
          styles={{
            root: {
              borderColor: "#E5E7EB",
              borderRadius: 8,
              minWidth: "auto",
              height: "auto",
            },
          }}
        >
          <IconArrowLeft size={18} color="#374151" />
        </Button>
        <Text fw={500} fz="xl">
          Collaboration Workspace
        </Text>
        <Button
          ml="auto"
          variant="outline"
          leftSection={<IconDownload size={18} />}
          loading={downloadingPDF}
          onClick={handleDownloadProfile}
        >
          Download Profile
        </Button>
      </Flex>

      <Box px="md" pt="md" style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}
        >
          <Tabs.List style={{ flexShrink: 0 }}>
            <Tabs.Tab
              value="overview"
              style={{
                color: activeTab === "overview" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-9)",
              }}
            >
              Overview
            </Tabs.Tab>
            <Tabs.Tab
              value="documents"
              style={{
                color: activeTab === "documents" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-9)",
              }}
            >
              Shared Documents
            </Tabs.Tab>
            <Tabs.Tab
              value="nda"
              style={{
                color: activeTab === "nda" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-9)",
              }}
            >
              NDA
            </Tabs.Tab>
            <Tabs.Tab
              value="questionnaire"
              style={{
                color:
                  activeTab === "questionnaire" ? "var(--mantine-color-primary-7)" : "var(--mantine-color-slate-9)",
              }}
            >
              Questionnaire
            </Tabs.Tab>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Panel value="overview" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {loadingCompany ? (
              <Center h="100%">
                <Loader />
              </Center>
            ) : (
              company && <MatchedProfileView company={company} containerSize="fluid" />
            )}
          </Tabs.Panel>

          {/* Documents Tab */}
          <Tabs.Panel value="documents" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {activeTab === "documents" && (
              <SharedDocumentsTab matchId={id} userCompanyName={userDetails?.company?.legalName} />
            )}
          </Tabs.Panel>

          {/* NDA Tab - Reuse existing NdaPage */}
          <Tabs.Panel value="nda" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {visitedTabs.has("nda") && (
              <NdaPage embedded otherCompanyId={otherCompanyId} collaboration={collaboration} />
            )}
          </Tabs.Panel>

          {/* Questionnaire Tab — always mounted so the modal can show on page load */}
          <Tabs.Panel value="questionnaire" pt="xl" style={{ flex: 1, overflow: "auto" }}>
            {collaboration && userDetails?.company?.id && (
              <QuestionnaireTab
                matchId={id}
                collaboration={collaboration}
                userCompanyId={userDetails.company.id}
              />
            )}
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Box>
  );
}
